
import { useState, useRef, useEffect } from 'react';
import { Search, Hash, CreditCard, Target, Building2, ArrowLeftRight, Calendar, PieChart, DollarSign, TrendingUp, Settings, FileText } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useGlobalSearch, SearchResult } from '@/hooks/useGlobalSearch';
import { cn } from '@/lib/utils';

export const GlobalSearch = () => {
  const [localQuery, setLocalQuery] = useState('');
  const [localIsOpen, setLocalIsOpen] = useState(false);
  const { search, handleResultClick } = useGlobalSearch();
  const [results, setResults] = useState<SearchResult[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  // Use useEffect to search when query changes, avoiding infinite loops
  useEffect(() => {
    if (localQuery && localQuery.length >= 2) {
      const searchResults = search(localQuery);
      setResults(searchResults);
    } else {
      setResults([]);
    }
  }, [localQuery, search]);

  const getIcon = (type: SearchResult['type']) => {
    switch (type) {
      case 'transaction': return ArrowLeftRight;
      case 'account': return Building2;
      case 'card': return CreditCard;
      case 'goal': return Target;
      case 'category': return Hash;
      case 'budget': return PieChart;
      case 'debt': return DollarSign;
      case 'future_release': return Calendar;
      case 'page': return FileText;
      default: return Search;
    }
  };

  const getTagColor = (type: SearchResult['type']) => {
    switch (type) {
      case 'transaction': return 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300';
      case 'account': return 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300';
      case 'card': return 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300';
      case 'goal': return 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300';
      case 'category': return 'bg-pink-100 text-pink-700 dark:bg-pink-900 dark:text-pink-300';
      case 'budget': return 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300';
      case 'debt': return 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300';
      case 'future_release': return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300';
      case 'page': return 'bg-gray-100 text-gray-700 dark:bg-gray-900 dark:text-gray-300';
      default: return 'bg-gray-100 text-gray-700 dark:bg-gray-900 dark:text-gray-300';
    }
  };

  const handleResultSelect = (result: SearchResult) => {
    handleResultClick(result);
    setLocalIsOpen(false);
    setLocalQuery('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalQuery(value);
    if (value.trim() && !localIsOpen) {
      setLocalIsOpen(true);
    }
  };

  return (
    <Popover open={localIsOpen} onOpenChange={setLocalIsOpen}>
      <PopoverTrigger asChild>
        <div className="relative w-full">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            ref={inputRef}
            placeholder="Buscar em tudo..."
            value={localQuery}
            onChange={handleInputChange}
            onClick={() => {
              if (localQuery && !localIsOpen) {
                setLocalIsOpen(true);
              }
            }}
            className="pl-10 w-full bg-background border-border text-base"
          />
        </div>
      </PopoverTrigger>
      
      <PopoverContent 
        className="w-[calc(100vw-3rem)] max-w-md sm:max-w-lg mx-2 p-0" 
        align="center"
        side="bottom"
        sideOffset={8}
        onOpenAutoFocus={(e) => e.preventDefault()}
        avoidCollisions={true}
        alignOffset={0}
      >
        {localQuery && (
          <ScrollArea className="max-h-[60vh] min-h-[200px] overflow-y-auto">
            <div className="p-2">
              {results.length > 0 ? (
                <div className="space-y-1">
                  {results.slice(0, 15).map((result) => {
                    const Icon = getIcon(result.type);
                    return (
                      <button
                        key={`${result.type}-${result.id}`}
                        onClick={() => handleResultSelect(result)}
                        className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-accent/50 text-left transition-colors focus:bg-accent/50 focus:outline-none"
                      >
                        <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm truncate">
                              {result.title}
                            </span>
                            <Badge 
                              variant="secondary" 
                              className={cn("text-xs px-2 py-0.5 flex-shrink-0", getTagColor(result.type))}
                            >
                              {result.tag}
                            </Badge>
                          </div>
                          {result.subtitle && (
                            <p className="text-xs text-muted-foreground truncate mt-0.5">
                              {result.subtitle}
                            </p>
                          )}
                        </div>
                      </button>
                    );
                  })}
                  {results.length > 15 && (
                    <div className="p-3 text-center text-xs text-muted-foreground border-t bg-muted/30">
                      <span className="font-medium">+{results.length - 15}</span> resultados adicionais
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  Nenhum resultado encontrado para "{localQuery}"
                </div>
              )}
            </div>
          </ScrollArea>
        )}
        
        {!localQuery && (
          <div className="px-3 py-2 pl-10 text-sm text-muted-foreground text-left">
            Digite para buscar transações, contas, metas e mais...
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
